import {useContext, useMemo, useState} from "react";
import {
  Arquivo,
  Campo,
  CampoType,
  EtapaImportacao,
  OptionsValidarCampo,
  SeparatorTypes
} from "../../types/ImportacaoPlanilha";
import {ProdutosPlanilhaContext} from "./ProdutosPlanilhaContext";
import {isTrue} from "../../services/Functions";
import {AuthContext} from "../Auth/AuthContext";

interface ProdutosPlanilhaProviderProps {
  children: JSX.Element;
}

const campos: Campo[] = [
  {
    value: "nome",
    label: "Nome",
    obrigatorio: true,
    type: "text",
    placeholder: "Preencher",
    validacao: {
      obrigatorio: true,
      maxLength: 64,
    }
  },
  {
    value: "sku",
    label: "SKU",
    type: "text",
    obrigatorio: true,
    placeholder: "Preencher",
    validacao: {
      obrigatorio: true,
      maxLength: 64,
      custom: (value, indexRow, indexCol, mappedCampos, dados) => {
        let variacaoRowIndex = mappedCampos.findIndex(campo => campo?.value === 'variacao');
        let subvariacaoRowIndex = mappedCampos.findIndex(campo => campo?.value === 'subvariacao');

        let skus = dados.map(row => row[indexRow])

        //verificar se já existe esse valor na coluna
        //pegar todos os index em que o valor é igual ao value
        let valuesRowsIndexes = skus.map((sku, index) => sku === value ? index : -1).filter(index => index !== -1);

        if(valuesRowsIndexes.length > 1) {
          let variacao = variacaoRowIndex >= 0 ? dados[indexCol][variacaoRowIndex] : '';
          let subvariacao = subvariacaoRowIndex >= 0 ? dados[indexCol][subvariacaoRowIndex] : '';
          //verificar se existe outra variacao+subvariacao igual pra esse sku
          let variacaoValuesRowsIndexes = valuesRowsIndexes.filter(index => {
            let rowVariacao = variacaoRowIndex >= 0 ? dados[index][variacaoRowIndex] : '';
            let rowSubvariacao = subvariacaoRowIndex >= 0 ? dados[index][subvariacaoRowIndex] : '';
            return rowVariacao === variacao && rowSubvariacao === subvariacao;
          });
          if(variacaoValuesRowsIndexes.length > 1) {
            const outrasLinhas = variacaoValuesRowsIndexes.filter(i => i !== indexCol).map(i => i + 1);
            const variacaoLabel = variacao ? ` e variação "${variacao}"` : ' sem variação';
            return `Linha duplicada: SKU "${value}"${variacaoLabel} já aparece na(s) linha(s) ${outrasLinhas.join(', ')}. Remova as duplicatas antes de importar.`;
          }
        }

        return true;
      }
    }
  },
  {
    value: "sku_variacao",
    label: "SKU | Variação",
    type: "text",
    obrigatorio: false,
    placeholder: "-",
    validacao: {
      maxLength: 64,
    }
  },
  {
    value: "tipo_variacao",
    label: "Tipo variação principal",
    type: "text",
    placeholder: "Não definida",
    obrigatorio: false,
    validacao: {
      maxLength: 64,
    }
  },
  {
    value: "variacao",
    label: "Valor variação principal",
    type: "text",
    placeholder: "-",
    obrigatorio: false,
    validacao: {
      maxLength: 64,
    }
  },
  {
    value: "tipo_subvariacao",
    label: "Tipo variação secundária",
    type: "text",
    placeholder: "Não definida",
    obrigatorio: false,
    validacao: {
      maxLength: 64,
    }
  },
  {
    value: "subvariacao",
    label: "Valor variação secundária",
    type: "text",
    placeholder: "-",
    obrigatorio: false,
    validacao: {
      maxLength: 64,
    }
  },
  {
    value: "preco",
    label: "Preço",
    type: "price",
    element: (dado) => {
      return (
        <>
          {dado.valid
            ? Number.parseFloat(dado.value.replace(",", ".")).toLocaleString(
                "pt-br",
                {
                  style: "currency",
                  currency: "BRL",
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }
              )
            : dado.value}
        </>
      );},
    placeholder: "Sob consulta",
    obrigatorio: false,
  },
  {
    value: "peso_g",
    label: "Peso (g)",
    type: "decimal",
    placeholder: "Preencher",
    obrigatorio: false,
    validacao: {
      obrigatorio: true,
    }
  },
  {
    value: "categoria",
    label: "Categoria",
    type: "text",
    obrigatorio: true,
    placeholder: "Preencher",
    validacao: {
      obrigatorio: true,
      maxLength: 64,
    }
  },
  {
    value: "subcategoria",
    label: "Subcategoria",
    type: "text",
    obrigatorio: false,
    placeholder: "-",
    validacao: {
      maxLength: 64,
    }
  },
  {
    value: "descricao",
    label: "Descrição",
    type: "quill",
    element: (dado) => {
      return(
        <div className="descricao" dangerouslySetInnerHTML={{__html: dado.value}}></div>
      )
    },
    placeholder: "-",
    obrigatorio: false,
  },
  {
    value: "estoque",
    label: "Estoque",
    type: "integer",
    placeholder: "Sem controle de estoque",
    obrigatorio: false,
  },
  {
    value: "multiplicador",
    label: "Multiplicador",
    type: "integer",
    placeholder: "0",
    obrigatorio: false,
  },
  {
    value: "quantidade_minima",
    label: "Quantidade mínima",
    type: "integer",
    placeholder: "0",
    obrigatorio: false,
  },
  {
    value: "solicitar_observacao",
    label: "Solicitar observação",
    type: "boolean",
    placeholder: "Não",
    obrigatorio: false,
    element: (dado) => {
        return (
            <>
              {isTrue(dado.value) ? 'Sim' : 'Não'}
            </>
        );
    }
  },
  {
    value: "tag_de_destaque",
    label: "Tag de destaque",
    type: "text",
    placeholder: "",
    obrigatorio: false,
    validacao: {
      maxLength: 20,
    }
  },
  {
    value: "caracteristicas",
    label: "Tags de característica",
    type: "text",
    placeholder: "Preencher",
    obrigatorio: false,
  }
];

const limparCampo = (campo: string) => {
  //remover ç, acentos, troca espaços por underline, remove paranteses e pipes e coloca tudo em minusculo, pra comparar com o value do select
  if(!campo) return '';
  return campo.trim()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[()|]/g, '')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

export const ProdutosPlanilhaProvider: React.FC<ProdutosPlanilhaProviderProps> = ({
  children
}) => {

  const [etapa, setEtapa] = useState<EtapaImportacao>("importar-arquivo");
  const [arquivo, setArquivo] = useState<Arquivo>({ name: "", content: "" });
  const [colunas, setColunas] = useState<string[]>([]);
  const [dados, setDados] = useState<string[][]>([]);
  const [separator, setSeparator] = useState<SeparatorTypes | "">("");
  const [tabelaSelecionada, setTabelaSelecionada] = useState<string>("");
  const [removerProdutos, setRemoverProdutos] = useState<boolean>(false);
  const [slidingActive, setSlidingActive] = useState<boolean>(false);

  const { permissoesPlanos } = useContext(AuthContext);

  const camposFiltrados = useMemo(() => {
    if (!permissoesPlanos?.SUBCATEGORIAS?.permitido) {
      return campos.filter(c => c.value !== 'subcategoria');
    }
    return campos;
  }, [permissoesPlanos]);

  const onLoadArquivo = async () => {
    //o replace é para remover as aspas do csv, que são adicionadas automaticamente
    //quando o campo tem caracter especial
    //tambem remove o \r, que é adicionado no final de cada linha em windows
    let rows: string[][] = 
      arquivo.content
        .split('\n')
        .map(row => row.split(separator || ';').map(value => value.replace(/^\"|\"$/g, '').replace(/\r$/, '')));
    
    //quando o result acaba com um \n, a última linha se torna um array com um elemento vazio
    //então é preciso remover essa linha
    if(rows[rows.length - 1].length === 1 && rows[rows.length - 1][0].trim() === '') {
      rows.pop();
    }

    //o relatório de produtos exporta "-" onde não existe valor (variação secundária,
    //subcategoria, preço sem tabela...). Se essa planilha voltar pra importação, o traço
    //viraria conteúdo de verdade (ex: variação secundária "-"), então a célula com apenas
    //um traço é tratada como vazia. A linha 0 é o cabeçalho e não entra nessa limpeza.
    rows = rows.map((row, index) => index === 0
      ? row
      : row.map(value => value.trim() === '-' ? '' : value));

    let biggestRow = rows.reduce((biggest, row) => row.length > biggest ? row.length : biggest, 0);
    //preenche as linhas com valores vazios, para que todas tenham o mesmo tamanho
    rows.forEach(row => {
      while(row.length < biggestRow || row.length < camposFiltrados.length) {
        row.push('');
      }
    });

    setColunas(rows[0]);
    setDados(rows.slice(1));
  };

  const validarCampo = (
      value: string,
      indexRow: number,
      indexCol: number,
      mappedCampos: Campo[],
      dados: string[][],
  ): { valid: boolean; reason?: string } => {
    const { type, validacao, label } = mappedCampos[indexRow];

    if (validacao && validacao.obrigatorio && value.trim() === "") {
      return { valid: false, reason: `${label} é obrigatório.` };
    }

    if ((['price', 'decimal', 'integer'].includes(type)) && isNaN(Number(value.replace(",", ".")))) {
      return { valid: false, reason: `${label} deve ser um número válido.` };
    }

    if(type === 'integer' && !Number.isInteger(Number(value.replace(",", ".")))) {
      return { valid: false, reason: `${label} deve ser um número inteiro.` };
    }

    if(validacao) {

      if (validacao.maxLength && value.length > validacao.maxLength) {
        return { valid: false, reason: `${label} deve ter no máximo ${validacao.maxLength} caracteres (atual: ${value.length}).` };
      }

      if (validacao.minLength && value.length < validacao.minLength) {
        return { valid: false, reason: `${label} deve ter no mínimo ${validacao.minLength} caracteres.` };
      }

      if (validacao.max && Number(value.replace(",", ".")) > validacao.max) {
        return { valid: false, reason: `${label} deve ser no máximo ${validacao.max}.` };
      }

      if (validacao.min && Number(value.replace(",", ".")) < validacao.min) {
        return { valid: false, reason: `${label} deve ser no mínimo ${validacao.min}.` };
      }
    }

    if(validacao?.custom) {
        const customValid = validacao.custom(value, indexRow, indexCol, mappedCampos, dados);
        if (customValid === false) {
            return { valid: false };
        }
        if (typeof customValid === 'string') {
            return { valid: false, reason: customValid };
        }
    }

    return { valid: true };
  }

  //montar os campos mapeados conforme os indexes, e garantir que nao haverá duplicidade
  //tenta casar pelo value (retrocompat) e também pelo label limpo (ex: "SKU | Produto" -> "sku_produto")
  const mappedCampos = useMemo(() => colunas.map(limparCampo).map((cleanColuna, index, self) => {
    return camposFiltrados.find((campo) => (
      (campo.value === cleanColuna || limparCampo(campo.label) === cleanColuna)
      && self.indexOf(cleanColuna) === index
    ));
  }), [colunas, camposFiltrados]);

  //campos que podem ser selecionados no select
  //adicionar um campo "Nenhum" no final
  const camposSelect = useMemo(() => [
    ...camposFiltrados.map(campo => ({
      name: campo.label + (campo.obrigatorio ? " *" : ""),
      value: campo.value
    })),
    {
      name: "Nenhum",
      value: "nenhum"
    }
  ], [camposFiltrados]);

  const dadosValidados = useMemo(() => dados.map((row, indexCol) => {
    return row.map((value, indexRow) => {
      const disabled = mappedCampos[indexRow] === undefined || mappedCampos[indexRow]?.value === 'nenhum';
      if (disabled) {
        return { value, valid: true, disabled: true, indexRow: indexRow, indexCol: indexCol};
      }
      const { valid, reason } = validarCampo(
          value,
          indexRow,
          indexCol,
          mappedCampos as Campo[],
          dados
      );

      return { value, disabled: false, valid, reason, indexRow: indexRow, indexCol: indexCol};
    });
  }), [dados, mappedCampos]);

  // checar se todos os campos obrigatorios foram selecionados, se nao, os campos com 'Nenhum' devem ser marcados como invalidos (vermelho)
  const qtdCamposObrigatoriosFaltantes = useMemo(() => camposFiltrados
  .filter(campo => campo.obrigatorio)
  .map(campo => campo.value)
  .filter(campo => !mappedCampos.map(mappedCampo => mappedCampo?.value).includes(campo)).length, [camposFiltrados, mappedCampos]);

  const qtdDadosInvalidos = useMemo(() => dadosValidados.filter(linha => linha.some(dado => !dado.valid)).length, [dadosValidados]);

  const qtdDadosValidos = useMemo(() => qtdCamposObrigatoriosFaltantes > 0 ? 0 : dadosValidados.filter(linha => linha.every(dado => dado.valid)).length, [qtdCamposObrigatoriosFaltantes, dadosValidados]);

  const qtdColunasInvalidas = useMemo(() => qtdCamposObrigatoriosFaltantes > 0 ?
    mappedCampos.filter(campo => campo === undefined || campo.value === 'nenhum').length :
    mappedCampos.filter((campo, index) => campo === undefined && colunas[index] !== '' && colunas[index] !== 'nenhum').length, [qtdCamposObrigatoriosFaltantes, mappedCampos, colunas]);

  return (
    <ProdutosPlanilhaContext.Provider
      value={{
        etapa,
        setEtapa,
        mappedCampos,
        camposSelect,
        dadosValidados,
        qtdCamposObrigatoriosFaltantes,
        qtdDadosInvalidos,
        qtdDadosValidos,
        qtdColunasInvalidas,
        onLoadArquivo,
        colunas,
        arquivo,
        setArquivo,
        setColunas,
        separator,
        setSeparator,
        dados,
        setDados,
        campos: camposFiltrados,
        tabelaSelecionada,
        setTabelaSelecionada,
        removerProdutos,
        setRemoverProdutos,
        slidingActive,
        setSlidingActive
      }}
    >
      {children}
    </ProdutosPlanilhaContext.Provider>
  );
};
